import api, { getServerUrl } from "./api";

export interface SiteBranding {
  siteName: string;
  siteTitle: string;
  siteFavicon: string;
  navbarLogo?: string;
  footerLogo?: string;
  favicon?: string;
}

// Cache for site branding data
let brandingCache: SiteBranding | null = null;
let lastFetchTime = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const normalizeBrandingAssetUrl = (
  value?: string,
  appOrigin = typeof window !== "undefined" ? window.location.origin : "",
  assetServer = getServerUrl(),
): string | undefined => {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;

  try {
    const parsed = new URL(trimmed, `${appOrigin.replace(/\/$/, "")}/`);

    // Uploaded branding used to be saved with the old HTTP hostname. All
    // /uploads assets now live behind the configured production server and R2.
    if (parsed.pathname.startsWith("/uploads/")) {
      const server = assetServer.replace(/\/$/, "");
      return `${server}${parsed.pathname}${parsed.search}${parsed.hash}`;
    }

    if (
      parsed.protocol === "http:" &&
      appOrigin.startsWith("https://") &&
      parsed.hostname === new URL(appOrigin).hostname
    ) {
      parsed.protocol = "https:";
    }

    return parsed.toString();
  } catch (_error) {
    return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  }
};

export const getSiteBranding = async (): Promise<SiteBranding> => {
  const now = Date.now();

  // Return cached data if still valid
  if (brandingCache && now - lastFetchTime < CACHE_DURATION) {
    return brandingCache;
  }

  try {
    const response = await api.get("/landing-page/content");
    const data = response.data || {};
    const settings = data.settings || {};
    const logos = data.logos || [];

    // Extract branding data from settings
    const siteName = settings.site_name || "ເພັດທະບຸນ ສະຫວັດ";
    const siteTitle = settings.site_title || "ເພັດທະບຸນ ສະຫວັດ ສູນການຮຽນຮູ້";
    const siteFavicon = settings.site_favicon || "/favicon.png";

    // Find navbar and footer logos
    const navbarLogo = normalizeBrandingAssetUrl(
      logos?.find(
        (logo: any) => logo.logo_type === "navbar" && logo.is_active,
      )?.logo_url,
    );
    const footerLogo = normalizeBrandingAssetUrl(
      logos?.find(
        (logo: any) => logo.logo_type === "footer" && logo.is_active,
      )?.logo_url,
    );
    const favicon = normalizeBrandingAssetUrl(
      logos?.find((logo: any) => logo.logo_type === "favicon" && logo.is_active)
        ?.logo_url || siteFavicon,
    );

    const branding: SiteBranding = {
      siteName,
      siteTitle,
      siteFavicon,
      navbarLogo,
      footerLogo,
      favicon,
    };

    // Update cache
    brandingCache = branding;
    lastFetchTime = now;

    return branding;
  } catch (error) {
    console.error("Error fetching site branding:", error);

    // Return default branding on error
    return {
      siteName: "ເພັດທະບຸນ ສະຫວັດ",
      siteTitle: "ເພັດທະບຸນ ສະຫວັດ ສູນການຮຽນຮູ້",
      siteFavicon: "/favicon.png",
    };
  }
};

export const updateDocumentHead = (branding: SiteBranding) => {
  // Update document title
  if (branding.siteTitle) {
    document.title = branding.siteTitle;
  }

  // Update favicon
  const faviconUrl = normalizeBrandingAssetUrl(
    branding.favicon || branding.siteFavicon,
  );

  if (faviconUrl) {
    const faviconLink = document.querySelector(
      'link[rel="icon"]',
    ) as HTMLLinkElement;
    if (faviconLink) {
      faviconLink.href = faviconUrl;
    } else {
      // Create favicon link if it doesn't exist
      const newFaviconLink = document.createElement("link");
      newFaviconLink.rel = "icon";
      newFaviconLink.href = faviconUrl;
      document.head.appendChild(newFaviconLink);
    }
  }

  // Update apple touch icon
  if (faviconUrl) {
    const appleTouchIcon = document.querySelector(
      'link[rel="apple-touch-icon"]',
    ) as HTMLLinkElement;
    if (appleTouchIcon) {
      appleTouchIcon.href = faviconUrl;
    } else {
      // Create apple touch icon link if it doesn't exist
      const newAppleTouchIcon = document.createElement("link");
      newAppleTouchIcon.rel = "apple-touch-icon";
      newAppleTouchIcon.href = faviconUrl;
      document.head.appendChild(newAppleTouchIcon);
    }
  }
};

export const clearBrandingCache = () => {
  brandingCache = null;
  lastFetchTime = 0;
};

export const refreshBranding = async () => {
  clearBrandingCache();
  const branding = await getSiteBranding();
  updateDocumentHead(branding);
  return branding;
};
